import React, { useEffect, useState } from 'react';
import { Table, Tag } from 'antd';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { fetchRobotsByAssetNumber } from './store/slice';
import { selectCellHistoryData } from './store/selectors';
import { ACTION_MODE } from '../../core/constants/constants';
import { format } from 'date-fns';

const CellHistory = ({ assetNumber, onActionClick, onCloseModal }) => {
  const [formData, setFormData] = useState([]);

  const cellData = useSelector(selectCellHistoryData);
  const dispatch = useDispatch();

  useEffect(() => {
    if (assetNumber) {
      dispatch(fetchRobotsByAssetNumber(assetNumber));
    }
  }, [assetNumber, dispatch]);

  useEffect(() => {
    setFormData(getAllChangesAcrossVersions(cellData));
  }, [cellData]);

  const handleViewClick = record => {
    onActionClick(record.currentData, ACTION_MODE.VIEW);
  };

  const handleCopyToCell = record => {
    onActionClick({ ...record.currentData, id: 0 }, ACTION_MODE.COPY_TO_CELL);
  };

  const formatDate = date => {
    if (!date) return '';
    try {
      return format(new Date(date), 'MM/dd/yyyy');
    } catch (err) {
      console.error('Invalid date:', date);
      return '';
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: text => <a>{formatDate(text)}</a>
    },
    {
      title: 'Changed By',
      dataIndex: 'changedBy',
      key: 'changedBy',
    },
    {
      title: 'Type',
      key: 'type',
      dataIndex: 'type',
      render: () => <Tag color="geekblue">Machine Details</Tag>,
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <div className="action-table-data d-flex">
          <div className="edit-delete-action">
            <Link className="me-2 p-2" onClick={() => handleViewClick(record)}>
              View
            </Link>
          </div>

          <div className="edit-delete-action">
            <Link className="me-2 p-2" onClick={() => handleCopyToCell(record)}>
              Copy to Cell
            </Link>
          </div>
        </div>
      ),
    },
  ];

  const getAllChangesAcrossVersions = records => {
    if (!records || records.length === 0) return [];

    const ignoredFields = ['id', 'userId', 'date', 'lastServicedBy', 'lastServiceDate'];
    const sortedRecords = [...records].sort((a, b) => new Date(b.date) - new Date(a.date));

    const allChanges = [];
    if (sortedRecords.length > 0) {
      const initialRecord = sortedRecords[0];
      allChanges.push({
        key: 1,
        date: formatDate(initialRecord.date),
        changedBy: initialRecord.lastServicedBy,
        changes: [],
        currentData: initialRecord,
        previousData: initialRecord,
      });
    }

    if (sortedRecords.length > 1) {
      for (let i = 1; i < sortedRecords.length; i++) {
        const prev = sortedRecords[i - 1];
        const curr = sortedRecords[i];
        let changes = [];

        for (const key in curr) {
          if (!ignoredFields.includes(key) && curr[key] !== prev[key]) {
            changes.push({
              field: key,
              oldValue: prev[key],
              newValue: curr[key],
            });
          }
        }
        allChanges.push({
          key: i + 1,
          date: formatDate(curr.date),
          changes: changes,
          changedBy: curr.lastServicedBy,
          currentData: curr,
          previousData: prev,
        });
      }
    }
    return allChanges;
  };

  return (
    <>
      <div className="row border m-3">
        <div className="col-lg-12 col-md-12 col-sm-12">
          <Table
            columns={columns}
            expandable={{
              expandedRowRender: record => {
                return (
                  <div>
                    <div className="d-grid">
                      <label className="fw-bold">
                        Original Creator:{' '}
                        <span className="">{record.previousData.lastServicedBy}</span>
                      </label>
                      <label className="fw-bold">
                        Original Creation Date:{' '}
                        <span className="">{formatDate(record.previousData.lastServiceDate)}</span>
                      </label>
                      <label className="fw-bold">Changes:</label>
                      {record.changes.map((change, index) => (
                        <label key={index} className="fw-bold ms-5">
                          {change.field}:
                          <span>
                            <span className="text-decoration-line-through text-red">
                              {' '}
                              {change.oldValue}
                            </span>{' '}
                            <span className="text-green"> {change.newValue}</span>{' '}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              },
              rowExpandable: record => record.name !== 'Not Expandable',
            }}
            dataSource={formData}
          />
        </div>
      </div>
    </>
  );
};

CellHistory.propTypes = {
  assetNumber: PropTypes.string,
  onActionClick: PropTypes.func,
  onCloseModal: PropTypes.func,
};

export default CellHistory;

